import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useTrace } from '../context/TraceContext';
import { useApp } from '../context/AppContext';
import TraceLabel from '../components/trace/TraceLabel';
import ReportExportButton from '../components/export/ReportExportButton';
import ReportSection from '../components/export/ReportSection';
import { 
  Settings, Wrench, AlertTriangle, CheckCircle2,
  Clock, ChevronRight
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { equipment, formatDate } from '../data/mockData';

const AssetsPage = () => {
  const { pidTraceMode } = useTrace();
  const { isReadOnly } = useApp();

  const getStatusColor = (status) => {
    switch (status) {
      case 'Operational': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Under Maintenance': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'Blocked': return 'bg-red-50 text-red-700 border-red-200';
      case 'Under Investigation': return 'bg-purple-50 text-purple-700 border-purple-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const getQualStatusColor = (status) => {
    switch (status) {
      case 'Qualified': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'Requalification Required': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  };

  const stats = {
    total: equipment.length,
    operational: equipment.filter(e => e.status === 'Operational').length,
    maintenance: equipment.filter(e => e.status === 'Under Maintenance').length,
    blocked: equipment.filter(e => e.status === 'Blocked').length
  };

  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Assets & Utilities
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Equipment qualification, calibration, and maintenance tracking
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* <ReportExportButton 
            title="Assets & Utilities Report"
            subtitle="Equipment qualification, calibration, and maintenance tracking"
          /> */}
        </div>
      </div>

      <ReportSection id="assets-metrics" title="Assets Metrics" description="Equipment status overview">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
            <TraceLabel prd="PRD §6.7" req="SM-EQP-001" />
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Total Equipment</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Operational</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.operational}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Under Maintenance</p>
              <p className="text-2xl font-bold text-amber-600">{stats.maintenance}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs text-slate-600 uppercase tracking-wider mb-1 font-medium">Blocked</p>
              <p className="text-2xl font-bold text-red-600">{stats.blocked}</p>
            </CardContent>
          </Card>
        </div>
      </ReportSection>

      <Tabs defaultValue="equipment">
        <TabsList className="bg-slate-100 border border-slate-200">
          <TabsTrigger value="equipment" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Settings className="h-4 w-4 mr-2" />
            Equipment
          </TabsTrigger>
          <TabsTrigger value="utilities" className="data-[state=active]:bg-[#fd4816] data-[state=active]:text-white">
            <Wrench className="h-4 w-4 mr-2" />
            Utilities
          </TabsTrigger>
        </TabsList>

        <TabsContent value="equipment" className="mt-4">
          <ReportSection id="equipment-list" title="Equipment List" description="All equipment with status and qualification">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.7" req="SM-EQP-001" />
              <Table>
              <TableHeader>
                <TableRow className="border-slate-200 hover:bg-transparent">
                  <TableHead className="text-slate-600">ID</TableHead>
                  <TableHead className="text-slate-600">Equipment</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-slate-600">Qualification</TableHead>
                  <TableHead className="text-slate-600">Calibration Due</TableHead>
                  <TableHead className="text-slate-600">Next PM</TableHead>
                  <TableHead className="text-slate-600">Deviations</TableHead>
                  <TableHead className="text-slate-600 w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {equipment.map(eq => (
                  <TableRow key={eq.id} className="hover:bg-slate-50 cursor-pointer" data-testid={`equipment-row-${eq.id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{eq.id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm text-slate-800 font-medium">{eq.name}</p>
                        <p className="text-xs text-slate-500">{eq.room}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(eq.status)} text-xs`}>{eq.status}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getQualStatusColor(eq.qualificationStatus)} text-xs`}>
                        {eq.qualificationStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(eq.calibrationDue)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{formatDate(eq.nextPM)}</TableCell>
                    <TableCell>
                      {eq.openDeviations > 0 ? (
                        <Badge className="bg-red-50 text-red-700 border-red-200 text-xs">
                          {eq.openDeviations}
                        </Badge>
                      ) : (
                        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      )}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          </ReportSection>
        </TabsContent>

        <TabsContent value="utilities" className="mt-4">
          <ReportSection id="utilities-monitoring" title="Utilities Monitoring" description="HVAC, water systems, compressed air, and other utilities">
            <Card className={`bg-white border border-slate-200 shadow-sm relative ${pidTraceMode ? 'border-cyan-300' : ''}`}>
              <TraceLabel prd="PRD §6.8" req="SM-HVAC-001" />
              <CardContent className="p-8 text-center">
                <Wrench className="h-12 w-12 mx-auto mb-4 text-slate-400" />
                <h3 className="text-lg font-medium text-slate-800 mb-2">Utilities Monitoring</h3>
                <p className="text-sm text-slate-600 mb-4">
                  HVAC, water systems, compressed air, and other utilities monitoring dashboard.
                </p>
                <p className="text-xs text-slate-500">Feature available in full implementation</p>
              </CardContent>
            </Card>
          </ReportSection>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssetsPage;
